<style scoped lang="less">
::v-deep {
    .vs__selected-options {
        flex-wrap: nowrap;
        white-space: nowrap;
        overflow: hidden;
        > .vs__selected {
            position: absolute;
            left: 1px;
        }
        > .vs__selected,
        > .vs__selected + .vs__search {
            font-size: var(--vs-font-size);
            margin: 0;
            padding: 0.375rem 0.75rem;
        }
    }
    .vs__dropdown-menu {
        position: absolute;
        z-index: 1002; // To avoid being overlapped by modal
        // add line-height to possible empty item text/label
        > .vs__dropdown-option span:before {
            content: "\200b"; /* insert a zero-width space */
            line-height: inherit;
        }
    }
    .vs__dropdown-toggle {
        height: calc(1.5em + 0.75rem + 2px);
        padding: 0;
        background: white;
    }
}
</style>
<template>
    <div class="form-group focus-form-group dropdown-form-group clearfix" :class="[settings.isChild ? 'form-group-child' : null, colClass, customErrorClass]">
        <p class="pdf-inline" v-if="isViewOnly">
            <strong class="display-label">{{ effectiveLabel || effectiveLabelText }}:</strong>
            <span class="display-value" v-show="effectiveValue">{{ effectiveValue }}</span>
            <button class="passthrough icon icon-edit mr-1" type="button" v-if="showAdminEditControls" @click.prevent="startAdminEdit"><span class="sr-only">Edit</span></button>
        </p>
        <template v-else>
            <div :class="[settings.isChild ? 'form-group-child' : null]">
                <label :aria-label="effectiveLabelText" class="control-label" :for="vmId" :class="{ dfRequired: settings.required, dfOptional: !settings.required, 'sr-only': effectiveLabelText != effectiveLabel }">
                    {{ effectiveLabelText }}
                    <HelpIcon :label="effectiveLabelText" :text="settings.helpText" />
                    <button class="passthrough icon icon-close mr-1" type="button" v-if="isAdminEditing" @click.prevent="cancelAdminEdit" aria-label="Stop Edit" />
                </label>
                <div v-if="isDropdownSearchable">
                    <v-select
                        ref="dropdown"
                        v-model="searchableDropdownValue"
                        @search="
                            () => {
                                dropdown.open = true;
                            }
                        "
                        @search:focus="openDropdown"
                        @search:blur="onBlur"
                        @option:selected="onOptionSelect"
                        :map-keydown="keydownHandlers"
                        :options="getAllItems"
                        :get-option-key="(option: any) => option.id"
                        :inputId="vmId"
                        :selectable="(option: any) => !option.hasOwnProperty('isGroupLabel')"
                        label="text"
                        :clearable="false"
                        :class="[settings.required ? 'dfRequired' : null, settings.placeholderText ? 'dfHasPlaceholder dfGhosted' : null]"
                    >
                        <template #option="{ text, groupName, isGroupLabel }">
                            <span v-if="isGroupLabel" class="font-weight-bold">
                                {{ groupName }}
                            </span>
                            <span v-else>
                                {{ text }}
                            </span>
                        </template>
                    </v-select>
                </div>
                <div v-else>
                    <select ref="dropdown" class="form-control" v-model="effectiveValue" @blur="data.markEdited()" :id="vmId" :aria-required="settings.required ? 'true' : 'false'" :aria-describedby="errorIds" :class="[settings.required ? 'dfRequired' : null, settings.placeholderText ? 'dfHasPlaceholder dfGhosted' : null]">
                        <option :value="item.effectiveValue" v-for="item in ungroupedItems">{{ item.text }}</option>
                        <optgroup :label="group.key" v-for="group in groupedItems">
                            <option :value="item.effectiveValue" v-for="item in group.items">{{ item.text }}</option>
                        </optgroup>
                    </select>
                </div>
            </div>
        </template>
        <ErrorList :data="data" :id="errorId" />
        <Comments :data="data" />
        <AdminSettingsDifferences :data="data" />
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, nextTick } from "vue";
import { useMainStore } from "@/mainStore";
import { useObservable } from "@vueuse/rxjs";
import { map } from "rxjs/operators";
import { props, Props, components, setup } from "./BaseDefMixin.3";
import { isNullOrWhitespace, stringCoalesce } from "@/lib/whitespaceMethods";
import type DropDownListDef from "../elements/DropDownListDef";
import type { DropDownListSettings } from "@cs-ts/drop-down-list-settings";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { ObservableReturnType } from "@/lib/observableHelpers";
import { computedAsync } from "@vueuse/core";
import delayPromise from "@/lib/delayPromise";

export default defineComponent({
    components: {
        ...components,
        "v-select": vSelect,
    },
    props: props,
    //@ts-ignore
    setup(props: Props<DropDownListDef, DropDownListSettings>) {
        const base = setup<DropDownListDef, DropDownListSettings>(props);

        const dropdown = ref<InstanceType<typeof vSelect>>(null!);

        const defaultValue = useObservable(props.data.defaultValue$().pipe(map(i => (i.length > 0 ? i[0] : null))));
        const hasValue = useObservable(props.data.hasValue$(), { initialValue: false });
        const groupedItems = useObservable(props.data.groupedItems$());
        const ungroupedItems = useObservable(props.data.ungroupedItems$());
        const effectiveLabel = useObservable(props.data.effectiveLabel$());
        const effectiveLabelText = useObservable(props.data.effectiveLabelText$());
        type groupedItemsType = ObservableReturnType<ReturnType<typeof props.data.groupedItems$>>;
        type itemType = groupedItemsType[0]["items"][0];

        const hasDefaultValue = computed(() => {
            return !isNullOrWhitespace(defaultValue.value);
        });
        const effectiveValue = computed({
            get() {
                return hasValue.value ? base.value.value : defaultValue.value;
            },
            set(value) {
                base.value.value = value as string | null;
            },
        });
        const effectivePlaceholderText = computed(() => {
            return stringCoalesce(defaultValue.value, base.settings.value.placeholderText);
        });

        type groupedItem = itemType | ({ groupName: string; isGroupLabel?: boolean } & Partial<itemType>);
        let cache = null as null | groupedItem[];
        const arraysEqual = (a: object[], b: object[]) => {
            return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => JSON.stringify(val) === JSON.stringify(b[index]));
        };
        const getAllItems = computed(() => {
            const groupedItemsI = (groupedItems.value || []).reduce((acc: groupedItem[], value: groupedItemsType[0]) => {
                acc.push({ groupName: value.key, isGroupLabel: true, text: null! });
                acc.push(...value.items);
                return acc;
            }, [] as groupedItem[]);

            const result = [...(ungroupedItems.value || []), ...groupedItemsI].map((item: groupedItem, id) => ({ ...item, id })) as groupedItem[];
            // TODO is this needed?
            if (cache == null || !arraysEqual(result, cache)) {
                cache = result;
            }
            return cache;
        });

        const searchableDropdownValue = computed({
            get() {
                return getAllItems.value?.find(item => item.effectiveValue == effectiveValue.value);
            },
            set(option) {
                effectiveValue.value = option!.effectiveValue;
            },
        });

        const mainStore = useMainStore();
        const isDropdownSearchable = computedAsync(() => {
            return mainStore.isFeatureFlagActiveAsync("SearchableDropdown");
        }, null);

        const openDropdown = async () => {
            // Set search input's own aria label and required data if no selected option
            const searchEl = dropdown.value.searchEl;
            const combobox = searchEl.closest("[role='combobox']");
            combobox.setAttribute("aria-label", effectiveLabelText.value);
            combobox.setAttribute("aria-describedby", base.errorIds.value);
            // TODO: Vue 2 version already has aria-controls on the combobox. Remove this when the Vue 3 updated version got this fix.
            combobox.setAttribute("aria-controls", combobox.getAttribute("aria-owns"));
            searchEl.setAttribute("required", base.settings.value.required.toString());

            // To handle first change event emitted from search element if ever user autofilled dropdown value
            searchEl.addEventListener("change", searchOnChange, { once: true, passive: true });

            await nextTick();
            dropdown.value.$el.querySelectorAll("ul").forEach((elem: HTMLElement) => {
                elem.setAttribute("aria-label", effectiveLabelText.value!);
                elem.setAttribute("aria-required", base.settings.value.required.toString());
                elem.setAttribute("aria-describedby", base.errorIds.value);
            });
        };
        const onBlur = async () => {
            // Remove event listener if autocomplete didn't trigger search element's change event
            dropdown.value.searchEl.removeEventListener("change", searchOnChange);
            await delayPromise(0);
            dropdown.value.open = false;
            props.data.markEdited();
        };
        const searchOnChange = (event: Event) => {
            const selectedOption = (cache ?? []).find(item => item.text == (event.target as HTMLInputElement).value);
            if (selectedOption) {
                dropdown.value.updateValue(selectedOption);
            }
        };
        // TODO: Remove if vue-select beta version is updated to support default searchEl focus on after option select
        const onOptionSelect = async () => {
            await openDropdown();
            focusSearchableDropdown();
        };
        const keydownHandlers = (keydownMap: Record<string, (event?: Event) => void>, dropdownVm: InstanceType<typeof vSelect>) => {
            return {
                ...keydownMap,
                // Tab
                9: () => {
                    dropdownVm.open = false;
                    keydownMap["9"]();
                },
                // Enter
                13: (event: Event) => {
                    if (dropdownVm.open) {
                        keydownMap["13"](event);
                        focusSearchableDropdown();
                    } else {
                        event.preventDefault();
                        dropdownVm.open = true;
                    }
                },
                // Spacebar
                // TODO: Remove if vue-select beta version is updated to support dropdown open on spacebar
                32: (event: Event) => {
                    if (!dropdownVm.open) {
                        event.preventDefault();
                        dropdownVm.open = true;
                    }
                },
                // Arrow Up
                // TODO: Remove if vue-select beta version is updated to support dropdown open on arrow up
                38: (event: Event) => {
                    keydownMap["38"](event);
                    dropdownVm.open = true;
                },
                // Arrow Down
                // TODO: Remove if vue-select beta version is updated to support dropdown open on arrow down
                40: (event: Event) => {
                    keydownMap["40"](event);
                    dropdownVm.open = true;
                },
            };
        };
        const doFocus = () => {
            if (dropdown.value == null) return;
            if (isDropdownSearchable.value == null) {
                setTimeout(doFocus, 100);
            } else {
                if (isDropdownSearchable.value) {
                    focusSearchableDropdown();
                } else {
                    dropdown.value.focus();
                }
            }
        };
        const focusSearchableDropdown = async () => {
            dropdown.value.searchEl.focus();
            await delayPromise(0);
            dropdown.value.open = false;
        };

        base.focus = doFocus;

        window.addEventListener(
            "focus",
            () => {
                if (document.activeElement == dropdown.value?.searchEl) {
                    doFocus();
                }
            },
            { passive: true },
        );

        return {
            dropdown,

            defaultValue,
            hasValue,
            groupedItems,
            ungroupedItems,
            effectiveLabel,
            effectiveLabelText,
            hasDefaultValue,
            effectiveValue,
            effectivePlaceholderText,
            getAllItems,
            searchableDropdownValue,
            isDropdownSearchable,
            keydownHandlers,
            openDropdown,
            onOptionSelect,
            onBlur,

            ...base.expose,
        };
    },
});
</script>
